import React, { Component } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import utils from "@utils";
import { OfficerService, GenericService } from "@services";
import { OFFICERS_ACTIONGRID, OFFICER_VETTED_STATUS } from "@constants";
import { withLDConsumer } from "launchdarkly-react-client-sdk";
import {
	HeaderGrid,
	Switch,
	EditAccountModal,
	Image,
	ScrollDetect,
	AsyncSelect,
	ReactSelect,
	ImpersonateModal,
} from "../../../components";
import "./OfficerList.scss";

const PROFILE_OPTIONS = lang => [
	{ value: true, label: lang.t("Completed") },
	{ value: false, label: lang.t("Incomplete") },
];

class OfficerList extends Component {
	constructor(props) {
		super(props);
		const {
			router: {
				location: { query },
			},
		} = props;

		let vettedStatus = "";
		// set initial vettedStatus based on url params
		switch (query.vettedStatus) {
			case "0":
				vettedStatus = 0;
				break;
			case "1":
				vettedStatus = 1;
				break;

			case "6":
				vettedStatus = 6;
				break;
			default:
				break;
		}

		this.state = {
			impersonatingUser: undefined,
			hasMore: true,
			officerCountLoading: false,
			USStates: [],
			officerList: [],
			officerRanks: [],
			officerCounts: {
				All: "",
				New: "",
				PendingApproval: "",
				PendingReverification: "",
			},
			name: "",
			searchFilters: {
				vettedStatus,
				OfficerRankId: "",
				profileCompleted: "",
				stateId: "",
				jurisdictionId: "",
				includeInactive: false,
				sortBy: "id",
				asc: false,
				pageNumber: 1,
				displayCount: 25,
				certificationTypeId: "",
				excludeMissingGovernmentId: false,
				excludeSupervisorApproval: false,
				onlyRKVerified: false,
			},
			officerCertifications: [],
		};
		this.cancel = undefined;
		this.loadJurisdictions = utils.loadJurisdictions;
	}

	componentDidMount = () => {
		const { user } = this.props;
		// Block this page for non admin users
		if (!user.IsStaffAdmin) {
			window.location.href = "/";
		}
		this.setState({ officerCountLoading: true });
		if (!user.isAgencyAdmin()) this.getOfficerCounts();
		this.getUSStates();
		this.getOfficerRanks();
		this.getOfficerCertifications();
	};

	/** Updates the URL and its parameters based on changes made to each filters */
	updateUrl = () => {
		const {
			searchFilters: { vettedStatus },
		} = this.state;
		const { router } = this.props;
		if (vettedStatus !== "") {
			router.push(`/officers?vettedStatus=${vettedStatus}`);
		} else {
			router.push("/officers");
		}
	};

	/**
	 * Updates the vetted status filter according to what the user clicks on (or selects on mobile) from the `<HeaderGrid>` component
	 * @param {Number} vettedStatus The officers vetted status indicating which vettwed status the user would like to filter list results by
	 */
	setActiveGrid = vettedStatus => {
		const { searchFilters } = this.state;
		// check if value is NaN since OFFICERS_ACTIONGRID has a type with an empty string value
		const officerType = OFFICERS_ACTIONGRID.find(
			officer => officer.type === parseInt(vettedStatus, 10),
		);
		this.setState(
			{
				hasMore: true,
				officerList: [],
				searchFilters: {
					...searchFilters,
					pageNumber: 1,
					vettedStatus: officerType?.type ?? "",
				},
			},
			() => {
				this.getOfficerCounts();
				this.updateUrl();
			},
		);
	};

	navigateToOfficerDetails = (officerId, e) => {
		const { router } = this.props;
		if (e.target.name !== "edit-button") {
			router.push(`/officers/${officerId}`);
		}
	};

	/**
	 * Displays the officers vetted status based on a comparison with the OFFICER_VETTED_STATUS object
	 * @param {Object} officerStatus The object containing details related to the officer vetted status
	 * @returns {HTMLElement} UI which will display the officers vetted status
	 */
	renderVettedStatus = officerStatus => {
		if (officerStatus) {
			return (
				<div
					className={`text-center label label-${officerStatus.color}`}
					style={{ display: "table", marginTop: "5px" }}
					title={officerStatus.label}
				>
					{officerStatus.label}
				</div>
			);
		}
		return <div />;
	};

	/**
	 * Handles all changes made to each filter in the officer list
	 * @param {Event} e The HTML event that triggered this method
	 * @param {Strung} name The name of the filter being changed
	 */
	handleFilterOnchange = (e, name) => {
		const { searchFilters } = this.state;
		const { router } = this.props;
		let value;
		let label;
		// if e returns null/undefined set initiate variables for value and label
		if (!e) {
			value = "";
			label = "";
		} else ({ value, label } = e);

		if (name === "vettedStatus") {
			if (value === 0 || value === 1 || value === 6) {
				router.push(`/officers?vettedStatus=${value}`);
			} else {
				router.push("/officers");
			}
		}

		if (name === "includeInactive") {
			value = !searchFilters.includeInactive;
		}

		if (name === "excludeMissingGovernmentId") {
			value = !searchFilters.excludeMissingGovernmentId;
		}

		if (name === "excludeSupervisorApproval") {
			value = !searchFilters.excludeSupervisorApproval;
		}

		if (name === "onlyRKVerified") {
			value = !searchFilters.onlyRKVerified;
		}

		if (name === "jurisdictionId") {
			this.setState(prevState => ({
				searchFilters: {
					...prevState.searchFilters,
					jurisdictionName: label,
				},
				officerCountLoading: false,
			}));
		}

		this.setState(
			prevState => ({
				officerList: [],
				hasMore: true,
				searchFilters: {
					...prevState.searchFilters,
					[name]: value,
					pageNumber: 1,
				},
			}),
			() => {
				this.getOfficerCounts();
			},
		);
	};

	handleSearchOnChange = e => {
		const { value } = e.target;
		this.setState({
			name: value,
		});
	};

	/** Searches for a specific officer in the list given the users input */
	handleOnSearch = () => {
		const { user } = this.props;

		this.setState(
			prevState => ({
				hasMore: true,
				searchFilters: {
					...prevState.searchFilters,
					pageNumber: 1,
				},
			}),
			() => this.getOfficerList(),
		);

		if (user.isSuperAdmin() || user.isLEA()) {
			this.getOfficerCounts();
		}
	};

	/**
	 * Updates the officers approval status
	 * @param {Number} id The unique id of the officer being updated
	 */
	updateOfficerApproval = id => e => {
		e.stopPropagation();
		const { lang } = global;

		OfficerService.updateOfficerApproval(id)
			.then(({ data }) => {
				const isApproved = data.IsApproved;

				utils.notify({
					type: "success",
					message: isApproved ? lang.t("Officer approved") : lang.t("Officer unapproved"),
				});
				this.setState(prevState => ({
					officerList: prevState.officerList.map(officer => {
						if (officer.Id === id) {
							return { ...officer, IsApplicableForJob: isApproved };
						}
						return officer;
					}),
				}));
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating Officer"),
					timeOut: 1500,
				});
			});
	};

	/** Retrieves the total number of officers for each vetted status based on selected filters */
	getOfficerCounts = () => {
		const { lang } = global;
		const { officerCounts, searchFilters, name } = this.state;

		const filters = {
			...searchFilters,
			name,
		};
		// filter out key value pairs which are empty as well as remove vettedStatus from params
		const filterParams = Object.fromEntries(
			Object.entries(filters).filter(
				([key, value]) => value !== "" && key !== "vettedStatus",
			),
		);
		OfficerService.getCounts(filterParams)
			.then(({ data }) => {
				this.setState({
					officerCounts: {
						...officerCounts,
						...data,
					},
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading Officer Counts"),
					timeOut: 1500,
				});
			})
			.finally(() => {
				this.setState({ officerCountLoading: false });
			});
	};

	/** Retrieves a paginated list of officers */
	getOfficerList = () => {
		const { lang } = global;
		const { user } = this.props;
		const {
			name,
			searchFilters,
			searchFilters: { pageNumber },
		} = this.state;
		const data = {
			FirstName: name,
			LastName: name,
		};
		// filter out key value pairs which are empty
		const filterParams = Object.fromEntries(
			Object.entries(searchFilters).filter(([, value]) => value !== ""),
		);
		filterParams.approved =
			user.isAgencyAdmin() &&
			name === "" &&
			!filterParams.jurisdictionId &&
			!filterParams.certificationTypeId
				? true
				: undefined;

		if (this.cancel) {
			this.cancel();
		}

		this.setState({ hasMore: true });
		OfficerService.search(filterParams, data, c => {
			this.cancel = c;
		})
			.then(({ data: resData }) => {
				this.cancel = undefined;
				this.setState(prevState => ({
					officerList:
						pageNumber > 1
							? [...prevState.officerList, ...resData.ItemList]
							: resData.ItemList,
					hasMore: resData.ItemList.length >= resData.DisplayCount,
					searchFilters: {
						...prevState.searchFilters,
						pageNumber: prevState.searchFilters.pageNumber + 1,
					},
				}));
			})
			.catch(error => {
				if (!axios.isCancel(error)) {
					this.setState(prevState => ({
						officerList: [],
						searchFilters: {
							...prevState.searchFilters,
							pageNumber: 1,
						},
						hasMore: false,
					}));
					utils.notify({
						type: "error",
						message: lang.t("Error loading officers"),
						timeOut: 1500,
					});
				}
			});
	};

	/** Retrieves a list of certifications that an officer has attained */
	getOfficerCertifications = () => {
		const { lang } = global;
		OfficerService.getCertifications()
			.then(({ data }) => {
				this.setState({
					officerCertifications: data.map(
						({ OfficerCertificationTypeName, OfficerCertificationTypeId }) => ({
							label: OfficerCertificationTypeName,
							value: OfficerCertificationTypeId,
						}),
					),
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error retrieving officer certifications"),
					title: "Error",
				});
			});
	};

	/** Sets the officer list back to its orginal state after closing out the Edit Officer Modal */
	reloadOfficerList = () =>
		this.setState(
			prevState => ({
				searchFilters: {
					...prevState.searchFilters,
					pageNumber: 1,
				},
			}),
			this.getOfficerList,
		);

	/** Retreives a list of ranks that can be applied to an offiecr when editing their details */
	getOfficerRanks = () => {
		const { lang } = global;
		OfficerService.getRanks()
			.then(({ data }) => {
				const officerRanksList = data.map(({ Value, Text }) => ({
					value: parseInt(Value, 10),
					label: Text,
				}));

				this.setState({
					officerRanks: officerRanksList,
				});
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading officer ranks"),
					timeOut: 1500,
				});
			});
	};

	/** Retrieves a list of al US states that will be selectable from a dropdown */
	getUSStates = () => {
		const { lang } = global;

		GenericService.getUSStates()
			.then(({ data }) => {
				const stateList = data.map(USState => ({
					value: USState.Id,
					label: `${USState.Abbreviation} - ${USState.Name}`,
				}));

				this.setState({ USStates: stateList });
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error loading US States"),
					title: "Error",
					timeOut: 1500,
				});
			});
	};

	toggleImpersonateModal = userId => {
		this.setState(prevState => {
			if (prevState.impersonatingUser) return { impersonatingUser: undefined };
			return { impersonatingUser: userId };
		});
	};

	render() {
		const { lang } = global;
		const { user } = this.props;
		const {
			impersonatingUser,
			USStates,
			hasMore,
			officerCounts,
			officerList,
			officerRanks,
			officerCountLoading,
			name,
			officerCertifications,
			searchFilters: {
				vettedStatus,
				includeInactive,
				OfficerRankId,
				profileCompleted,
				stateId,
				certificationTypeId,
				excludeSupervisorApproval,
				excludeMissingGovernmentId,
				onlyRKVerified,
			},
		} = this.state;

		return (
			<div
				className="container"
				id="officerList-container"
			>
				{/* Title */}
				<div className="row">
					<div className="col-xs-12">
						<h2>{lang.t("Officer Management")}</h2>
					</div>
				</div>
				{/* Title */}

				{/* Grid */}
				<If condition={user.isSuperAdmin() || user.isLEA()}>
					<HeaderGrid
						gridOptions={OFFICERS_ACTIONGRID}
						loadingCounts={officerCountLoading}
						counts={officerCounts}
						activeGrid={vettedStatus}
						setActive={this.setActiveGrid}
					/>
				</If>
				{/* Grid */}

				{/* Officer Search */}
				<div id="officerList-search">
					<div className="row">
						<If condition={user.isSuperAdmin() || user.isLEA() || user.isAgencyAdmin()}>
							<div className="col-sm-4 col-md-3">
								<div className="input-group">
									<span className="input-label">{lang.t("Search Officer")}</span>
									<input
										id="search"
										className="form-control"
										type="search"
										name="search"
										placeholder={lang.t("Search")}
										value={name}
										onChange={e => this.handleSearchOnChange(e)}
										onKeyPress={e => {
											if (e.key === "Enter") {
												this.handleOnSearch();
											}
										}}
										data-test-id="officer__search-field"
									/>
									<span className="input-group-btn">
										<button
											className="btn btn-default"
											type="button"
											onClick={() => this.handleOnSearch()}
											data-test-id="officer__search-button"
										>
											<i className="rkf rkf-search" />
										</button>
									</span>
								</div>
							</div>
						</If>

						<If condition={user.isSuperAdmin() || user.isLEA()}>
							<div className="col-sm-4 col-md-3">
								<span>
									{lang.t("dynamic.Filter by", {
										property: lang.t("Vetted Status"),
									})}
								</span>
								<ReactSelect
									id="vettedStatus"
									dataTestId="officer__vetted-status"
									name="vettedStatus"
									options={OFFICER_VETTED_STATUS}
									onChange={e => this.handleFilterOnchange(e, "vettedStatus")}
									value={vettedStatus}
									isClearable
								/>
							</div>
						</If>

						<If condition={user.isSuperAdmin() || user.isLEA()}>
							<div className="col-sm-4 col-md-3">
								<span>
									{lang.t("dynamic.Filter by", { property: lang.t("Rank") })}
								</span>
								<ReactSelect
									id="OfficerRankId"
									dataTestId="officer__rank"
									name="OfficerRankId"
									options={officerRanks}
									onChange={e => this.handleFilterOnchange(e, "OfficerRankId")}
									value={OfficerRankId}
									isClearable
								/>
							</div>
						</If>

						<If condition={user.isSuperAdmin()}>
							<div className="col-sm-4 col-md-3">
								<span>
									{lang.t("dynamic.Filter by", {
										property: lang.t("Profile Completed"),
									})}
								</span>
								<ReactSelect
									id="profileCompleted"
									dataTestId="officer__profile-completed"
									name="profileCompleted"
									options={PROFILE_OPTIONS(lang)}
									onChange={e => this.handleFilterOnchange(e, "profileCompleted")}
									value={profileCompleted}
									isClearable
								/>
							</div>

							<div className="col-sm-4 col-md-3">
								<span>
									{lang.t("dynamic.Filter by", { property: lang.t("State") })}
								</span>
								<ReactSelect
									id="stateId"
									dataTestId="officer__state"
									name="stateId"
									options={USStates}
									onChange={e => this.handleFilterOnchange(e, "stateId")}
									value={stateId}
									isClearable
								/>
							</div>
						</If>
						<div className="col-sm-4 col-md-3">
							<span>
								{lang.t("dynamic.Filter by", {
									property: lang.t("Certifications"),
								})}
							</span>
							<ReactSelect
								id="certificationTypeId"
								dataTestId="officer__certification-type"
								name="certificationTypeId"
								options={officerCertifications}
								onChange={e => this.handleFilterOnchange(e, "certificationTypeId")}
								value={certificationTypeId}
								isClearable
							/>
						</div>
						<If condition={user.isSuperAdmin() || user.isAgencyAdmin()}>
							<div className="col-sm-4 col-md-3">
								<span>
									{lang.t("dynamic.Filter by", {
										property: lang.t("Jurisdiction"),
									})}
								</span>
								<AsyncSelect
									id="jurisdictionId"
									dataTestId="officer__jurisdiction"
									name="jurisdictionId"
									placeholder={lang.t("Search")}
									cacheOptions
									loadOptions={this.loadJurisdictions}
									onChange={e => this.handleFilterOnchange(e, "jurisdictionId")}
									isClearable
								/>
							</div>
						</If>
					</div>
					<div className="separator separator-solid hidden-xs" />

					<div className="row">
						<If condition={user.isSuperAdmin() || user.isLEA()}>
							<div className="col-sm-3 col-lg-2">
								<div className="input-group checkbox-container">
									<input
										id="includeInactive"
										data-test-id="officer__include-inactive"
										name="includeInactive"
										type="checkbox"
										value={includeInactive}
										onChange={e =>
											this.handleFilterOnchange(e, "includeInactive")
										}
									/>
									<label
										htmlFor="includeInactive"
										className="checkbox-text"
									>
										{lang.t("Include Inactive")}
									</label>
								</div>
							</div>
						</If>
						<If condition={user.isSuperAdmin()}>
							<div className="col-sm-3 col-lg-2">
								<div className="input-group checkbox-container">
									<input
										id="onlyRKVerified"
										data-test-id="officer__only-rk-verified"
										name="onlyRKVerified"
										type="checkbox"
										value={onlyRKVerified}
										onChange={e =>
											this.handleFilterOnchange(e, "onlyRKVerified")
										}
									/>
									<label
										htmlFor="onlyRKVerified"
										className="checkbox-text"
									>
										{lang.t("Only RK Verified")}
									</label>
								</div>
							</div>
						</If>
						<If condition={user.isSuperAdmin() || user.isLEA()}>
							<div className="col-sm-3">
								<div className="input-group checkbox-container">
									<input
										id="excludeMissingGovernmentId"
										data-test-id="officer__exclude-missing-government-id"
										name="excludeMissingGovernmentId"
										type="checkbox"
										checked={excludeMissingGovernmentId}
										value={excludeMissingGovernmentId}
										onChange={e =>
											this.handleFilterOnchange(
												e,
												"excludeMissingGovernmentId",
											)
										}
									/>
									<label
										htmlFor="excludeMissingGovernmentId"
										className="checkbox-text"
									>
										{lang.t("Exclude Missing Government ID")}
									</label>
								</div>
							</div>
							<div className="col-sm-3 col-lg-4">
								<div className="input-group checkbox-container">
									<input
										id="excludeSupervisorApproval"
										data-test-id="officer__exclude-supervisor-approval"
										name="excludeSupervisorApproval"
										type="checkbox"
										checked={excludeSupervisorApproval}
										value={excludeSupervisorApproval}
										onChange={e =>
											this.handleFilterOnchange(
												e,
												"excludeSupervisorApproval",
											)
										}
									/>
									<label
										htmlFor="excludeSupervisorApproval"
										className="checkbox-text"
									>
										{lang.t("Exclude Missing Supervisor Approval")}
									</label>
								</div>
							</div>
						</If>
					</div>
				</div>
				{/* Officer Search */}

				{/* Table Container */}
				<div id="officerList-table">
					<div
						id="officerList-table-header"
						className="hidden-xs"
					>
						<div className="row">
							<If condition={user.isSuperAdmin()}>
								<div className="col-sm-1">
									<strong>ID</strong>
								</div>
							</If>
							<div className={user.isLEA() ? "col-sm-2" : "col-sm-1"}>
								<strong>PID</strong>
							</div>
							<div className="col-sm-3">
								<strong>{lang.t("Officer")}</strong>
							</div>
							<If condition={user.isSuperAdmin() || user.isAgencyAdmin()}>
								<div className="col-sm-2">
									<strong>{lang.t("Department")}</strong>
								</div>
							</If>
							<div className="col-sm-2 col-md-3">
								<strong>{lang.t("Contact")}</strong>
							</div>
							<If condition={user.isSuperAdmin() || user.isLEA()}>
								<div className={user.isLEA() ? "col-sm-2" : "col-sm-1"}>
									<strong>{lang.t("Status")}</strong>
								</div>
							</If>
							<If condition={user.isAgencyAdmin()}>
								<div className="col-sm-1 text-right">
									<strong>{lang.t("Approve")}</strong>
								</div>
							</If>
						</div>
					</div>
					<div id="officerList-table-body">
						<If condition={user.isSuperAdmin() || user.isLEA() || user.isAgencyAdmin()}>
							<For
								index="index"
								each="officer"
								of={officerList}
							>
								<div
									className="officerList-item"
									key={`officerList-item-${index}`}
									role="row"
									onClick={e => this.navigateToOfficerDetails(officer.Id, e)}
									onKeyPress={e => {
										if (e.key === "Enter") {
											this.navigateToOfficerDetails(officer.Id, e);
										}
									}}
									tabIndex="0"
								>
									<div className="row">
										<If condition={user.isSuperAdmin()}>
											<div className="col-xs-12 col-sm-1 officer-id">
												<strong className="item-description">ID:</strong>
												{/* Using nbsp uniode '\u00A0', to ensure the width of columns is correct on mobile devices */}
												<button
													type="button"
													className="btn btn-link imp-user"
													onClick={e => {
														e.stopPropagation();
														this.toggleImpersonateModal(officer.Id);
													}}
												>
													{officer.Id ?? "\u00A0"}
												</button>
											</div>
										</If>
										<div
											className={
												user.isLEA()
													? "col-xs-12 col-sm-2 officer-pid"
													: "col-xs-12 col-sm-1 officer-pid"
											}
										>
											{officer.OfficerNumber && (
												<strong className="item-description">PID:</strong>
											)}
											<span title={officer.OfficerNumber}>
												{officer.OfficerNumber ?? "\u00A0"}
											</span>
										</div>
										<div className="col-xs-12 col-sm-4 col-md-3">
											<div className="col-xs-3 col-sm-4 officer-image">
												<Image
													className="img-circle lazyloaded"
													src={officer.ContactPhotoUrl ?? "\u00A0"}
													fallbackSrc="avatar"
												/>
											</div>
											<div className="col-xs-9 col-sm-8 officer-name">
												<strong>
													{`${officer.FirstName} ${officer.LastName}`}
												</strong>
												<If condition={officer.IsRKPay}>
													&nbsp;
													<span className="label label-primary">
														RKPay
													</span>
												</If>
												<div>
													<small className="subtext">
														{officer.OfficerRank ?? "\u00A0"}
													</small>
												</div>
												<Choose>
													<When
														condition={
															officer.VettedStatus === 2 ||
															officer.VettedStatus === 8
														}
													>
														<div>
															<small className="subtext">
																{`${"Verified"}
																${utils.formatDate(`${officer.VettedDateTime}Z`, "en-US", user.TimeZoneId, "numeric")}`}
															</small>
														</div>
													</When>
													<When condition={officer.VettedStatus === 0}>
														<div>
															<small className="subtext">
																{`${"Created"}
																${utils.formatDate(`${officer.CreateDateUTC}Z`, "en-US", user.TimeZoneId, "numeric")}`}
															</small>
														</div>
													</When>
												</Choose>
											</div>
										</div>
										<If condition={user.isSuperAdmin() || user.isAgencyAdmin()}>
											<div className="col-xs-12 col-sm-2 officer-dept">
												<div>
													<strong className="item-description">
														Department:
													</strong>
													<strong>
														{officer.Department ?? "\u00A0"}
													</strong>
												</div>
												<div>
													<small className="subtext">
														{`${officer.City ?? "\u00A0"}${officer.City && officer.State ? ", " : ""}${officer.State ?? "\u00A0"}`}
													</small>
												</div>
											</div>
										</If>
										<div className="col-sm-2 col-md-3 officer-contact col-xs-12">
											<strong className="item-description">Contact:</strong>
											<div
												className="officer-email"
												title={`${officer.EmailAddress}`}
											>
												{officer.EmailAddress ?? "\u00A0"}
											</div>
											<div>
												{utils.formatPhoneNumber(officer.MobilePhone) ??
													"\u00A0"}
											</div>
										</div>
										<If condition={user.isLEA() || user.isSuperAdmin()}>
											<div
												className={`officer-status ${user.isLEA() ? "col-xs-6 col-sm-2" : "col-xs-6 col-sm-1"}`}
												style={{ display: "block" }}
											>
												{officer.VettedStatus === 8 &&
													this.renderVettedStatus(
														OFFICER_VETTED_STATUS.find(
															item => item.value === 2,
														),
													)}
												{this.renderVettedStatus(
													OFFICER_VETTED_STATUS.find(
														item => item.value === officer.VettedStatus,
													),
												)}
											</div>
										</If>
										<If condition={user.isLEA() || user.isSuperAdmin()}>
											<div className="col-xs-12 col-sm-2 col-md-1 text-center">
												<button
													id={`edit-button-${index}`}
													name="edit-button"
													type="button"
													className="btn btn-primary btn-block"
													onClick={() => this._userModal.open(officer.Id)}
												>
													{lang.t("Edit")}
												</button>
											</div>
										</If>
										<If condition={user.isAgencyAdmin()}>
											<div className="col-xs-12 col-sm-1 text-right">
												<div>
													<strong className="item-description">
														{`${lang.t("Approve")}:`}
													</strong>
												</div>
												<Switch
													onClick={this.updateOfficerApproval(officer.Id)}
													on={officer.IsApplicableForJob}
												/>
											</div>
										</If>
									</div>
								</div>
							</For>
							<If condition={hasMore}>
								<ScrollDetect
									className="text-center"
									onVisible={this.getOfficerList}
								>
									<i className="glyphicon glyphicon-repeat rkf-spin rkf-fw" />
								</ScrollDetect>
							</If>
						</If>
					</div>
				</div>
				{/* Table Container */}
				<EditAccountModal
					accountType="officer"
					user={user}
					ref={ref => {
						this._userModal = ref;
					}}
					onFinished={this.reloadOfficerList}
				/>
				<ImpersonateModal
					userIdFromList={impersonatingUser}
					show={impersonatingUser !== undefined}
					toggleModal={this.toggleImpersonateModal}
				/>
			</div>
		);
	}
}

OfficerList.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object.isRequired,
};

OfficerList.defaultProps = {
	user: {},
};

export default withLDConsumer()(OfficerList);
